/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { AIMessageDraft } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import AutoSendToNewMatches from 'bundles/enterprise-admin-messaging-v2/components/common/AutoSendToNewMatches';
import { useMessagingTrackingData } from 'bundles/enterprise-admin-messaging-v2/hooks/useMessagingTrackingData';
import {
  MESSAGING_BUTTON,
  MESSAGING_CHECKBOX,
  MESSAGING_MODAL,
  MESSAGING_RADIO,
} from 'bundles/enterprise-admin-messaging-v2/utils/eventing';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  draft: AIMessageDraft;
  updateDraft: (partial: Partial<AIMessageDraft>) => void;
  onDone: () => void;
  /**
   * Whether to show the "auto-send to new matches" automation option. Defaults
   * to true; entry points that shouldn't offer automation (e.g. the
   * program-card reminder) pass false to hide it.
   */
  showAutomate?: boolean;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  supportText: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
};

/**
 * Final confirmation screen. Confirms the send count and lets the admin
 * opt in to auto-sending the same message to new learners that match this
 * audience in the future, on a selectable cadence.
 */
const MessageSentScreen = ({ draft, updateDraft, onDone, showAutomate = true }: Props) => {
  const track = useTracker();
  const trackingData = useMessagingTrackingData();

  // Modal impression for the post-send confirmation screen.
  React.useEffect(() => {
    track('view_modal', { ...trackingData, modal: { name: MESSAGING_MODAL.SEND_SUCCESS } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Track the auto-send toggle flip, then persist it on the draft. */
  const handleEnabledChange = React.useCallback<(autoSendEnabled: boolean) => void>(
    (autoSendEnabled) => {
      track('click_checkbox', {
        checkbox: { name: MESSAGING_CHECKBOX.AUTO_SEND_TOGGLE, checked: autoSendEnabled },
        ...trackingData,
        modal: { name: MESSAGING_MODAL.SEND_SUCCESS },
      });
      updateDraft({ autoSendEnabled });
    },
    [track, trackingData, updateDraft]
  );

  /** Track the cadence selection, then persist it on the draft. */
  const handleCadenceChange = React.useCallback<(autoSendCadence: AIMessageDraft['autoSendCadence']) => void>(
    (autoSendCadence) => {
      track('change_radio', {
        radio: { id: MESSAGING_RADIO.AUTO_SEND_FREQUENCY, value: autoSendCadence },
        ...trackingData,
        modal: { name: MESSAGING_MODAL.SEND_SUCCESS },
      });
      updateDraft({ autoSendCadence });
    },
    [track, trackingData, updateDraft]
  );

  /** Track the Done completion, then run the close handler. */
  const handleDone = React.useCallback<() => void>(() => {
    track('click_button', {
      button: { name: MESSAGING_BUTTON.DONE },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.SEND_SUCCESS },
    });
    onDone();
  }, [track, trackingData, onDone]);

  return (
    <React.Fragment>
      <Dialog.Content>
        <div css={styles.body}>
          <Typography2 component="p" variant="bodyPrimary" css={styles.supportText}>
            <FormattedMessage
              message={_t('Your message has been sent to {count, plural, one {# learner} other {# learners}}.')}
              count={draft.audienceCount}
            />
          </Typography2>

          {showAutomate && (
            <AutoSendToNewMatches
              enabled={draft.autoSendEnabled}
              cadence={draft.autoSendCadence}
              onEnabledChange={handleEnabledChange}
              onCadenceChange={handleCadenceChange}
            />
          )}
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <Button variant="primary" onClick={handleDone} data-testid="ai-message-modal-done">
          {_t('Done')}
        </Button>
      </Dialog.Actions>
    </React.Fragment>
  );
};

export default MessageSentScreen;
